import React, { useState, useEffect, useCallback } from 'react';
import { VERSION_INFO } from '../constants/version';
import { getErrorMessage } from '../utils/errorHandler';

interface Daikubun {
  daikubun_id: number;
  daikubun_code: string;
  daikubun_name: string;
  is_active: boolean;
}

interface ClassicDaikubunManagementProps {
  onMenuSelect: (menuId: string) => void;
  tenantName: string;
  currentUser: any;
  apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  currentTenantId: number | null;
}

const ClassicDaikubunManagement: React.FC<any> = ({
  onMenuSelect,
  tenantName,
  currentUser,
  apiFetch,
  currentTenantId,
}) => {
  const [daikubunList, setDaikubunList] = useState<Daikubun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDaikubun, setSelectedDaikubun] = useState<Daikubun | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingData, setEditingData] = useState<Daikubun | null>(null);

  // 大区分データの取得
  const fetchDaikubunData = useCallback(async () => {
    if (currentTenantId == null) {
      setDaikubunList([]);
      setSelectedDaikubun(null);
      setEditingData(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await apiFetch('/api/daikubun');
      if (!response.ok) {
        const message = await getErrorMessage(response, '大区分データの取得に失敗しました');
        throw new Error(message);
      }
      const data = await response.json();
      const mapped: Daikubun[] = data.map((item: any) => ({
        daikubun_id: item.id,
        daikubun_code: item.daikubun_code,
        daikubun_name: item.daikubun,
        is_active: item.shiyou_kubun !== false,
      }));
      setDaikubunList(mapped);
      setError(null);
    } catch (err: any) {
      setError(err.message || '大区分データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, currentTenantId]);

  useEffect(() => {
    fetchDaikubunData();
  }, [fetchDaikubunData]);

  // 検索フィルター
  const filteredDaikubun = daikubunList.filter(daikubun =>
    daikubun.daikubun_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    daikubun.daikubun_code.includes(searchTerm)
  );

  const handleDaikubunSelect = (daikubun: Daikubun) => {
    setSelectedDaikubun(daikubun);
    setEditingData({ ...daikubun });
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!editingData || !selectedDaikubun) return;

    try {
      const payload: Record<string, unknown> = {};
      if (editingData.daikubun_name !== undefined) {
        payload.daikubun = editingData.daikubun_name;
      }
      if (editingData.daikubun_code !== undefined) {
        payload.daikubun_code = editingData.daikubun_code;
      }
      if (editingData.is_active !== undefined) {
        payload.shiyou_kubun = editingData.is_active;
      }
      const response = await apiFetch(`/api/daikubun/${selectedDaikubun.daikubun_id}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const message = await getErrorMessage(response, '保存に失敗しました');
        throw new Error(message);
      }

      // データを再取得
      await fetchDaikubunData();
      setIsEditing(false);
      setError(null);
    } catch (err: any) {
      setError(err.message || '保存に失敗しました');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (selectedDaikubun) {
      setEditingData({ ...selectedDaikubun });
    }
  };

  // 一覧のチェックボックスで有効/無効をトグル（勘定科目マスタと同じ方式）
  const handleToggleDaikubunActive = async (daikubun: Daikubun) => {
    try {
      const response = await apiFetch(`/api/daikubun/${daikubun.daikubun_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shiyou_kubun: !daikubun.is_active }),
      });
      if (!response.ok) {
        const message = await getErrorMessage(response, '状態の更新に失敗しました');
        throw new Error(message);
      }
      setDaikubunList(prev =>
        prev.map(d =>
          d.daikubun_id === daikubun.daikubun_id ? { ...d, is_active: !d.is_active } : d
        )
      );
      if (selectedDaikubun?.daikubun_id === daikubun.daikubun_id) {
        setSelectedDaikubun(prev => (prev ? { ...prev, is_active: !prev.is_active } : null));
        setEditingData(prev => (prev && prev.daikubun_id === daikubun.daikubun_id ? { ...prev, is_active: !prev.is_active } : prev));
      }
    } catch (err: any) {
      setError(err?.message || '状態の更新に失敗しました');
    }
  };

  const handleInputChange = (field: keyof Daikubun, value: any) => {
    if (editingData) {
      setEditingData({
        ...editingData,
        [field]: value
      });
    }
  };

  return (
    <div className="classic-daikubun-management" style={{
      backgroundColor: '#f0f0f0',
      minHeight: '100vh',
      fontFamily: "'MS UI Gothic', 'Hiragino Kaku Gothic ProN', sans-serif",
      fontSize: '11px'
    }}>
      {/* ヘッダー部分 */}
      <div className="classic-header" style={{
        backgroundColor: '#004499',
        color: 'white',
        padding: '8px 15px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '2px solid #004499'
      }}>
        <div>
          <h1 style={{
            margin: 0,
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            大区分マスタ管理
          </h1>
          <div style={{ fontSize: '18px', fontWeight: 'bold', marginTop: '4px' }}>
            {tenantName}
          </div>
        </div>
        <div style={{ fontSize: '10px', textAlign: 'right' }}>
          <div>System ver {VERSION_INFO.version}</div>
          <div>DB ver 3</div>
          <div style={{ marginTop: '4px', color: '#ffff99' }}>
            ユーザー: {currentUser?.username || 'admin'}
          </div>
        </div>
      </div>

      {/* メインコンテンツ */}
      <div style={{ padding: '15px', display: 'flex', gap: '15px', height: 'calc(100vh - 80px)' }}>
        {/* 左側：大区分一覧 */}
        <div style={{ flex: '1', minWidth: '350px' }}>
          <fieldset style={{
            border: '2px inset #e0e0e0',
            backgroundColor: '#ffffff',
            padding: '10px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <legend style={{
              fontWeight: 'bold',
              fontSize: '12px',
              padding: '0 5px'
            }}>
              大区分一覧
            </legend>

            {/* 検索ボックス */}
            <div style={{ marginBottom: '10px' }}>
              <label style={{ fontSize: '11px', marginRight: '5px' }}>検索:</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="大区分名またはコードで検索"
                style={{
                  padding: '2px 4px',
                  fontSize: '11px',
                  border: '1px inset #e0e0e0',
                  width: '200px'
                }}
              />
            </div>

            {/* エラー表示 */}
            {error && (
              <div style={{
                backgroundColor: '#ffe6e6',
                color: '#cc0000',
                padding: '5px',
                marginBottom: '10px',
                border: '1px solid #cc0000',
                fontSize: '11px'
              }}>
                {error}
              </div>
            )}

            {/* データ表示エリア */}
            <div style={{ flex: 1, overflow: 'auto', border: '1px inset #e0e0e0' }}>
              {loading ? (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                  データを読み込み中...
                </div>
              ) : (
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '11px'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: '#e0e0e0' }}>
                      <th style={{ border: '1px solid #999', padding: '4px', width: '80px' }}>コード</th>
                      <th style={{ border: '1px solid #999', padding: '4px' }}>大区分名</th>
                      <th style={{ border: '1px solid #999', padding: '4px', width: '50px' }}>有効</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDaikubun.map((daikubun) => (
                      <tr
                        key={daikubun.daikubun_id}
                        onClick={() => handleDaikubunSelect(daikubun)}
                        style={{
                          cursor: 'pointer',
                          backgroundColor: selectedDaikubun?.daikubun_id === daikubun.daikubun_id ? '#cce7ff' : 'white'
                        }}
                        onMouseOver={(e) => {
                          if (selectedDaikubun?.daikubun_id !== daikubun.daikubun_id) {
                            e.currentTarget.style.backgroundColor = '#f0f8ff';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (selectedDaikubun?.daikubun_id !== daikubun.daikubun_id) {
                            e.currentTarget.style.backgroundColor = 'white';
                          }
                        }}
                      >
                        <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'center' }}>
                          {daikubun.daikubun_code}
                        </td>
                        <td style={{ border: '1px solid #ccc', padding: '4px' }}>
                          {daikubun.daikubun_name}
                        </td>
                        <td style={{ border: '1px solid #ccc', padding: '4px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={daikubun.is_active}
                            onChange={(e) => { e.stopPropagation(); handleToggleDaikubunActive(daikubun); }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* 件数表示 */}
            <div style={{ marginTop: '5px', fontSize: '10px', color: '#666' }}>
              表示件数: {filteredDaikubun.length} / 全{daikubunList.length}件
            </div>
          </fieldset>
        </div>

        {/* 右側：詳細・編集エリア */}
        <div style={{ flex: '0 0 300px' }}>
          <fieldset style={{
            border: '2px inset #e0e0e0',
            backgroundColor: '#ffffff',
            padding: '10px',
            height: '100%'
          }}>
            <legend style={{
              fontWeight: 'bold',
              fontSize: '12px',
              padding: '0 5px'
            }}>
              詳細情報
            </legend>

            {selectedDaikubun && editingData ? (
              <div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>
                    大区分ID:
                  </label>
                  <input
                    type="text"
                    value={editingData.daikubun_id}
                    readOnly
                    style={{
                      width: '100%',
                      padding: '2px 4px',
                      fontSize: '11px',
                      border: '1px solid #ccc',
                      backgroundColor: '#f5f5f5'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>
                    大区分コード:
                  </label>
                  <input
                    type="text"
                    value={editingData.daikubun_code}
                    onChange={(e) => handleInputChange('daikubun_code', e.target.value)}
                    readOnly={!isEditing}
                    style={{
                      width: '100%',
                      padding: '2px 4px',
                      fontSize: '11px',
                      border: isEditing ? '1px inset #e0e0e0' : '1px solid #ccc',
                      backgroundColor: isEditing ? 'white' : '#f5f5f5'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>
                    大区分名:
                  </label>
                  <input
                    type="text"
                    value={editingData.daikubun_name}
                    onChange={(e) => handleInputChange('daikubun_name', e.target.value)}
                    readOnly={!isEditing}
                    style={{
                      width: '100%',
                      padding: '2px 4px',
                      fontSize: '11px',
                      border: isEditing ? '1px inset #e0e0e0' : '1px solid #ccc',
                      backgroundColor: isEditing ? 'white' : '#f5f5f5'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ fontSize: '11px' }}>
                    <input
                      type="checkbox"
                      checked={editingData.is_active}
                      onChange={(e) => handleInputChange('is_active', e.target.checked)}
                      disabled={!isEditing}
                      style={{ marginRight: '5px' }}
                    />
                    有効
                  </label>
                </div>

                {/* ボタン */}
                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                  {!isEditing ? (
                    <button
                      onClick={handleEdit}
                      style={{
                        backgroundColor: '#e0e0e0',
                        border: '2px outset #e0e0e0',
                        padding: '4px 8px',
                        fontSize: '11px',
                        cursor: 'pointer'
                      }}
                      onMouseDown={(e) => e.currentTarget.style.border = '2px inset #e0e0e0'}
                      onMouseUp={(e) => e.currentTarget.style.border = '2px outset #e0e0e0'}
                      onMouseLeave={(e) => e.currentTarget.style.border = '2px outset #e0e0e0'}
                    >
                      編集
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleSave}
                        style={{
                          backgroundColor: '#e0e0e0',
                          border: '2px outset #e0e0e0',
                          padding: '4px 8px',
                          fontSize: '11px',
                          cursor: 'pointer'
                        }}
                        onMouseDown={(e) => e.currentTarget.style.border = '2px inset #e0e0e0'}
                        onMouseUp={(e) => e.currentTarget.style.border = '2px outset #e0e0e0'}
                        onMouseLeave={(e) => e.currentTarget.style.border = '2px outset #e0e0e0'}
                      >
                        保存
                      </button>
                      <button
                        onClick={handleCancel}
                        style={{
                          backgroundColor: '#e0e0e0',
                          border: '2px outset #e0e0e0',
                          padding: '4px 8px',
                          fontSize: '11px',
                          cursor: 'pointer'
                        }}
                        onMouseDown={(e) => e.currentTarget.style.border = '2px inset #e0e0e0'}
                        onMouseUp={(e) => e.currentTarget.style.border = '2px outset #e0e0e0'}
                        onMouseLeave={(e) => e.currentTarget.style.border = '2px outset #e0e0e0'}
                      >
                        キャンセル
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                大区分を選択してください
              </div>
            )}
          </fieldset>
        </div>
      </div>

      {/* 下部ボタン */}
      <div style={{
        position: 'absolute',
        bottom: '10px',
        left: '15px',
        right: '15px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <button
          onClick={() => onMenuSelect('main-menu')}
          style={{
            backgroundColor: '#e0e0e0',
            border: '2px outset #e0e0e0',
            padding: '6px 12px',
            fontSize: '11px',
            cursor: 'pointer',
            fontFamily: 'MS UI Gothic, sans-serif'
          }}
          onMouseDown={(e) => e.currentTarget.style.border = '2px inset #e0e0e0'}
          onMouseUp={(e) => e.currentTarget.style.border = '2px outset #e0e0e0'}
          onMouseLeave={(e) => e.currentTarget.style.border = '2px outset #e0e0e0'}
        >
          メインメニューに戻る
        </button>

        <div style={{ fontSize: '10px', color: '#666' }}>
          最終更新: {new Date().toLocaleString('ja-JP')}
        </div>
      </div>
    </div>
  );
};

export default ClassicDaikubunManagement;
